/**
 * Hook for managing BidWin score caching
 *
 * This hook provides:
 * - Check if scores need calculation
 * - Trigger batch score calculation with progress
 * - Cache statistics
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  needsScoreRecalculation,
  batchCalculateAllScores,
  getScoreCacheStats,
  invalidateUserScores
} from '../lib/score-cache-service';

interface ScoreCacheState {
  isCalculating: boolean;
  progress: number; // 0-100
  totalContracts: number;
  processedContracts: number;
  needsRecalculation: boolean;
  lastCalculated: string | null;
  cachedCount: number;
  error: string | null;
}

export function useScoreCache() {
  const { user, profile } = useAuth();
  const [state, setState] = useState<ScoreCacheState>({
    isCalculating: false,
    progress: 0,
    totalContracts: 0,
    processedContracts: 0,
    needsRecalculation: false,
    lastCalculated: null,
    cachedCount: 0,
    error: null
  });

  // Track if we've already started background calculation
  const hasStartedCalculation = useRef(false);

  // Check if scores need recalculation
  const checkNeedsRecalculation = useCallback(async () => {
    if (!user) return;

    try {
      const needs = await needsScoreRecalculation(user.id);
      const stats = await getScoreCacheStats(user.id);

      setState(prev => ({
        ...prev,
        needsRecalculation: needs,
        lastCalculated: stats.lastUpdated,
        cachedCount: stats.totalCached
      }));
    } catch (error) {
      console.error('Error checking score cache status:', error);
    }
  }, [user]);

  // Start batch score calculation
  const calculateScores = useCallback(async () => {
    if (!user || !profile || state.isCalculating) return;

    setState(prev => ({
      ...prev,
      isCalculating: true,
      progress: 0,
      error: null
    }));

    try {
      const result = await batchCalculateAllScores(
        user.id,
        profile,
        (completed, total) => {
          setState(prev => ({
            ...prev,
            processedContracts: completed,
            totalContracts: total,
            progress: Math.round((completed / total) * 100)
          }));
        }
      );

      if (result.success) {
        const stats = await getScoreCacheStats(user.id);
        setState(prev => ({
          ...prev,
          isCalculating: false,
          needsRecalculation: false,
          lastCalculated: stats.lastUpdated,
          cachedCount: stats.totalCached,
          progress: 100
        }));
      } else {
        setState(prev => ({
          ...prev,
          isCalculating: false,
          error: `Score calculation had ${result.errors} errors`
        }));
      }
    } catch (error) {
      console.error('Error calculating scores:', error);
      setState(prev => ({
        ...prev,
        isCalculating: false,
        error: error instanceof Error ? error.message : 'Failed to calculate scores'
      }));
    }
  }, [user, profile, state.isCalculating]);

  // Invalidate all scores (force recalculation)
  const invalidateScores = useCallback(async () => {
    if (!user) return;

    try {
      await invalidateUserScores(user.id);
      setState(prev => ({
        ...prev,
        needsRecalculation: true,
        cachedCount: 0,
        lastCalculated: null
      }));
    } catch (error) {
      console.error('Error invalidating scores:', error);
    }
  }, [user]);

  // Check status on mount and when user changes
  useEffect(() => {
    if (user && profile) {
      checkNeedsRecalculation();
    }
  }, [user, profile, checkNeedsRecalculation]);

  // Auto-start calculation in background if needed (only once)
  useEffect(() => {
    if (
      user &&
      profile &&
      state.needsRecalculation &&
      !state.isCalculating &&
      !hasStartedCalculation.current
    ) {
      hasStartedCalculation.current = true;
      // Start calculation in background after a short delay
      const timer = setTimeout(() => {
        calculateScores();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [user, profile, state.needsRecalculation, state.isCalculating, calculateScores]);

  return {
    ...state,
    calculateScores,
    invalidateScores,
    checkNeedsRecalculation
  };
}
